import { useState, useEffect, useCallback } from "react";
import useAPI from "hooks/useAPI.js";
import OrganizationService from "services/OrganizationService";
import { useSelector } from "react-redux";
import { selectHospital } from "state/slices/hospitalSlice";
import { Paper, Grid, Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import ColumnBarChart from "components/charts/ColumnBarChart";
import PieChart from "components/charts/PieChart";
import Typography from "@mui/material/Typography";
import HospitalStatsChartsUtils from "utils/HospitalStatsChartDataUtils";
import HOSPITAL_STATS_CHART_PROPS from "utils/constants/HospitalStatsChartProps";

function Dashboard() {
  const theme = useTheme();
  const focusedHospital = useSelector(selectHospital);
  const [statsData, setStatsData] = useState({
    adolescentPatientsCount: 0,
    adultPatientsCount: 0,
    childPatientsCount: 0,
    patientsCount: 0,
    completedDoctorAssessmentsCount: 0,
    completedEnrollmentSessionsCount: 0,
    last4WeeksCompletedDoctorAssessmentForms: [],
    last4WeeksCompletedEnrollmentSessions: [],
    referralSourceCounts: {},
    referralSourcePercentages: {},
  });

  const fetchHospitalStatsAPI = useAPI(
    OrganizationService.getHospitalStats,
    fetchHospitalStatsHandler,
    false
  );

  const fetchHospitalStats = useCallback(() => {
    fetchHospitalStatsAPI.execute(focusedHospital.id);
  }, [fetchHospitalStatsAPI, focusedHospital.id]);

  useEffect(() => {
    if (focusedHospital.id !== "") {
      // Fetch stats only after hospital has been set
      fetchHospitalStats();
    }

    //Set chart categories
    setLast4WeekBasedChartCategories(
      HOSPITAL_STATS_CHART_PROPS.doctorAsmtForms
    );
    setLast4WeekBasedChartCategories(
      HOSPITAL_STATS_CHART_PROPS.enrollmentSessions
    );
  }, [focusedHospital.id]);

  /* API Success Handlers */
  function fetchHospitalStatsHandler(response) {
    setStatsData(response.data);
  }

  const referralSourceCodes = HospitalStatsChartsUtils.REFERRAL_SOURCE_CODES || [];
  const referralCounts = referralSourceCodes.map(
    (code) => (statsData.referralSourceCounts && statsData.referralSourceCounts[code]) || 0
  );

  if (fetchHospitalStatsAPI.status === "pending" && !fetchHospitalStatsAPI.response) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          minHeight: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Row 1: quick stat numbers */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  backgroundColor: theme.palette.background.default,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.main}
                >
                  Total Patients
                </Typography>
                <Typography variant="h2" color={theme.palette.text.secondary}>
                  {statsData.patientsCount}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  backgroundColor: theme.palette.background.default,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.main}
                >
                  Assessments Completed
                </Typography>
                <Typography variant="h2" color={theme.palette.text.secondary}>
                  {statsData.completedDoctorAssessmentsCount}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  backgroundColor: theme.palette.background.default,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color={theme.palette.text.main}
                >
                  Sessions Completed
                </Typography>
                <Typography variant="h2" color={theme.palette.text.secondary}>
                  {statsData.completedEnrollmentSessionsCount}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Row 2: age group + referral sources */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: theme.palette.background.default,
              p: 2,
              height: "350px",
            }}
          >
            <Typography
              variant="subtitle2"
              color={theme.palette.text.main}
            >
              Patients by Age Group
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <PieChart
                chartProps={HOSPITAL_STATS_CHART_PROPS.patientAgeGroup}
                dataLabels={
                  HOSPITAL_STATS_CHART_PROPS.patientAgeGroup.dataLabels
                }
                data={HospitalStatsChartsUtils.getPatientAgeGroupDataSeries(
                  statsData
                )}
              />
              <Box sx={{ ml: 3 }}>
                <Legend
                  labels={HOSPITAL_STATS_CHART_PROPS.patientAgeGroup.dataLabels}
                  colors={HOSPITAL_STATS_CHART_PROPS.patientAgeGroup.colors}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: theme.palette.background.default,
              p: 2,
              height: "350px",
            }}
          >
            <Typography
              variant="subtitle2"
              color={theme.palette.text.main}
            >
              Referral Sources (%)
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <PieChart
                chartProps={HOSPITAL_STATS_CHART_PROPS.referralSources}
                dataLabels={
                  HOSPITAL_STATS_CHART_PROPS.referralSources.dataLabels
                }
                data={HospitalStatsChartsUtils.getReferralSourcePercentageSeries(
                  statsData
                )}
              />
              <Box sx={{ ml: 3 }}>
                <Legend
                  labels={HOSPITAL_STATS_CHART_PROPS.referralSources.dataLabels}
                  colors={HOSPITAL_STATS_CHART_PROPS.referralSources.colors}
                  counts={referralCounts}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Row 3: bar charts */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: theme.palette.background.default,
              p: 2,
            }}
          >
            <ColumnBarChart
              chartProps={HOSPITAL_STATS_CHART_PROPS.doctorAsmtForms}
              data={HospitalStatsChartsUtils.getLast4WeeksDataSeries(
                statsData.last4WeeksCompletedDoctorAssessmentForms
              )}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: theme.palette.background.default,
              p: 2,
            }}
          >
            <ColumnBarChart
              chartProps={HOSPITAL_STATS_CHART_PROPS.enrollmentSessions}
              data={HospitalStatsChartsUtils.getLast4WeeksDataSeries(
                statsData.last4WeeksCompletedEnrollmentSessions
              )}
            />
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
}

function setLast4WeekBasedChartCategories(chartProps) {
  chartProps.xaxis.categories =
    HospitalStatsChartsUtils.getLast4WeeksAsXaxisCategories();
}

function Legend({ labels, colors, counts }) {
  if (!labels || !colors) return null;

  return (
    <>
      {labels.map((label, index) => (
        <Box
          key={label}
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 0.75,
          }}
        >
          <Box
            sx={{
              width: 13,
              height: 13,
              borderRadius: "50%",
              bgcolor: colors[index],
              mr: 1,
            }}
          />
          <Typography variant="subtitle2" fontWeight={400} color="text.secondary">
            {label}
            {typeof counts?.[index] === "number" && <> - {counts[index]}</>}
          </Typography>
        </Box>
      ))}
    </>
  );
}

export default Dashboard;
